import { useState } from 'react';

export default function CTASection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', phone: '', company: '', comments: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const toggleFaq = (index: number) => setOpenFaq(openFaq === index ? null : index);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    const phoneRegex = /^\+?\d{10,15}$/;
    return phoneRegex.test(cleanPhone);
  };

  const normalizePhone = (phone: string): string => {
    let cleanPhone = phone.replace(/[^\d+]/g, '');
    if (!cleanPhone.startsWith('+')) cleanPhone = '+' + cleanPhone;
    return cleanPhone;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { setErrorMessage('Пожалуйста, укажите ваше имя'); return; }
    if (!formData.phone.trim()) { setErrorMessage('Пожалуйста, укажите ваш телефон'); return; }
    if (!privacyAccepted) { setErrorMessage('Пожалуйста, подтвердите согласие на обработку персональных данных'); return; }
    if (!validatePhone(formData.phone)) { setErrorMessage('Пожалуйста, введите корректный номер телефона (минимум 10 цифр)'); return; }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const honeypotValue = (document.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value || '';
      if (honeypotValue.trim()) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', company: '', comments: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
        return;
      }

      const normalizedPhone = normalizePhone(formData.phone);
      const baseUrl = 'https://businesstrainer.bitrix24.by/rest/1/yxbm0u72myjrxywc/crm.lead.add.json';
      const params = new URLSearchParams({
        'fields[NAME]': formData.name,
        'fields[TITLE]': `Лид с businesstraining chto-takoe-korporativnoe-obuchenie ${formData.name}`,
        'fields[COMPANY_TITLE]': formData.company,
        'fields[PHONE][0][VALUE]': normalizedPhone,
        'fields[COMMENTS]': formData.comments,
        'params[REGISTER_SONET_EVENT]': 'Y',
        'ASSIGNED_BY_ID': '1'
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`, { method: 'GET' });
      if (response.ok) {
        const result = await response.json();
        if (result.result) {
          setFormStatus('success');
          setFormData({ name: '', phone: '', company: '', comments: '' });
          setTimeout(() => setFormStatus('idle'), 5000);
        } else {
          throw new Error('Ошибка отправки');
        }
      } else {
        throw new Error('Ошибка отправки');
      }
    } catch {
      setFormStatus('error');
      setErrorMessage('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
      setTimeout(() => { setFormStatus('idle'); setErrorMessage(''); }, 5000);
    }
  };

  const whyChooseCards = [
    { icon: 'ri-tools-fill', title: 'Практика вместо теории', desc: '80% времени обучения — практические упражнения, кейсы и отработка навыков на реальных задачах вашего бизнеса.' },
    { icon: 'ri-focus-2-line', title: 'Программы под задачи бизнеса', desc: 'Не типовые решения, а индивидуальные программы, разработанные после диагностики и аудита.' },
    { icon: 'ri-file-search-line', title: 'Подготовка до обучения', desc: 'Глубокая диагностика потребностей и аудит компетенций до старта — чтобы обучение было точным.' },
    { icon: 'ri-search-eye-line', title: 'Аудит', desc: 'Анализ текущих бизнес-процессов, компетенций команды и точек роста для максимальной релевантности программы.' },
    { icon: 'ri-user-heart-line', title: 'Сопровождение', desc: 'Посттренинговое сопровождение и поддержка внедрения — чтобы навыки закрепились в ежедневной работе.' },
    { icon: 'ri-bar-chart-grouped-line', title: 'Измеримые результаты', desc: 'Оценка эффективности по бизнес-показателям: конверсия, средний чек, удовлетворённость клиентов.' },
    { icon: 'ri-award-line', title: '18+ лет опыта', desc: 'Более 18 лет практики в корпоративном обучении, продажах и управлении. Сотни успешных проектов.' },
    { icon: 'ri-global-line', title: 'Беларусь, Россия, Казахстан', desc: 'Работаем с компаниями в Беларуси, России и Казахстане в офлайн и онлайн форматах.' }
  ];

  const faqItems = [
    { q: 'Что такое корпоративное обучение сотрудников?', a: 'Корпоративное обучение — это система развития профессиональных знаний, навыков и компетенций сотрудников компании, направленная на достижение бизнес-целей организации. Это комплексный подход к развитию персонала, включающий тренинги, семинары, воркшопы, бизнес-игры, наставничество, коучинг и другие форматы.' },
    { q: 'Чем обучение отличается от тренинга?', a: 'Тренинг — это один из форматов корпоративного обучения, сфокусированный на отработке конкретных навыков через практические упражнения. Корпоративное обучение — более широкое понятие, включающее все форматы развития сотрудников: от тренингов и семинаров до наставничества, коучинга и онлайн-курсов.' },
    { q: 'Когда компании нужно корпоративное обучение?', a: 'Корпоративное обучение необходимо, когда: снижается конверсия продаж, растёт текучесть кадров, появляются новые продукты, компания масштабируется, сотрудники не справляются с новыми задачами, падает вовлечённость, происходят изменения на рынке. Обучение должно быть системным процессом, а не разовой акцией.' },
    { q: 'Как понять, что сотрудники нуждаются в обучении?', a: 'Признаки: снижение KPI, рост ошибок, конфликты между отделами, низкая конверсия, невыполнение планов, жалобы клиентов, высокая текучесть кадров, низкая вовлечённость. Для точной диагностики проводится аудит компетенций и анализ бизнес-показателей.' },
    { q: 'Можно ли обучать сотрудников онлайн?', a: 'Да, онлайн-обучение — эффективный формат корпоративного обучения для распределённых команд. Современные платформы позволяют проводить интерактивные тренинги, бизнес-игры и воркшопы онлайн с сохранением качества. Смешанное обучение сочетает очные и дистанционные форматы для максимальной эффективности.' },
    { q: 'Как выбрать программу корпоративного обучения?', a: 'Выбор программы начинается с диагностики потребностей бизнеса: анализ KPI, аудит компетенций, опросы сотрудников. На основе данных формируется индивидуальная программа, которая может включать тренинги по продажам, переговорам, управлению, soft skills или комплексное развитие команды.' },
    { q: 'Сколько длится корпоративное обучение?', a: 'Продолжительность зависит от целей и формата. Тренинг может длиться 1-3 дня, комплексная программа развития — от нескольких недель до нескольких месяцев. Важно, чтобы обучение было не разовым событием, а системным процессом с этапами внедрения и сопровождения.' },
    { q: 'Как оценить эффективность обучения?', a: 'Эффективность оценивается по модели Киркпатрика: реакция участников, усвоение знаний, изменение поведения, бизнес-результаты. Ключевые метрики: конверсия, средний чек, удовлетворённость клиентов, повторные продажи, снижение ошибок, вовлечённость сотрудников.' },
    { q: 'Что такое смешанное обучение (blended learning)?', a: 'Смешанное обучение — это сочетание очных форматов с онлайн-инструментами: вебинары, видеоуроки, интерактивные задания. Такой подход позволяет охватить больше сотрудников, снизить затраты и поддерживать непрерывность развития персонала.' },
    { q: 'Какие бывают форматы корпоративного обучения?', a: 'Основные форматы: тренинги, семинары, воркшопы, бизнес-игры, фасилитация, наставничество, коучинг, онлайн-обучение, смешанное обучение, микрообучение. Выбор формата зависит от целей обучения, специфики бизнеса и уровня сотрудников.' },
    { q: 'Можно ли обучать руководителей?', a: 'Да, развитие управленческих компетенций — одно из ключевых направлений корпоративного обучения. Программы для руководителей включают стратегическое мышление, управление командой, делегирование, эффективную коммуникацию, принятие решений и мотивацию сотрудников.' },
    { q: 'Можно ли адаптировать программу под специфику компании?', a: 'Да, все программы корпоративного обучения адаптируются под отрасль, размер компании, уровень сотрудников и конкретные бизнес-задачи. Мы проводим предварительную диагностику и аудит, чтобы программа была максимально релевантной и эффективной.' }
  ];

  return (
    <>
      {/* Почему компании выбирают Business Training */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Почему компании выбирают BusinessTraining.by
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Системный подход, измеримые результаты и 18+ лет экспертизы в корпоративном обучении
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseCards.map((card, i) => (
              <div key={i} className="bg-white p-7 rounded-xl hover:shadow-md transition-shadow group">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${card.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA с формой */}
      <section id="contact" className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-10 md:p-16 shadow-xl">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6">
                Хотите понять, какое обучение подойдёт именно вашей компании?
              </h2>
              <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
                Получите бесплатную консультацию. Мы проведём диагностику, определим точки роста и предложим программу, которая даст измеримые результаты.
              </p>
            </div>
            <div className="max-w-2xl mx-auto">
              <form id="pillar_contact_form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-xl p-5 sm:p-8 shadow-lg">
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <label htmlFor="pillar_name" className="block text-sm font-semibold text-gray-900 mb-2">Имя <span className="text-red-500">*</span></label>
                    <input type="text" id="pillar_name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base" placeholder="Введите ваше имя" />
                  </div>
                  <div>
                    <label htmlFor="pillar_phone" className="block text-sm font-semibold text-gray-900 mb-2">Телефон <span className="text-red-500">*</span></label>
                    <input type="tel" id="pillar_phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base" placeholder="+375 (XX) XXX-XX-XX" />
                  </div>
                  <div>
                    <label htmlFor="pillar_company" className="block text-sm font-semibold text-gray-900 mb-2">Название компании</label>
                    <input type="text" id="pillar_company" name="company" value={formData.company} onChange={handleInputChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base" placeholder="Введите название компании" />
                  </div>
                  <div>
                    <label htmlFor="pillar_comments" className="block text-sm font-semibold text-gray-900 mb-2">Запрос</label>
                    <textarea id="pillar_comments" name="comments" value={formData.comments} onChange={handleInputChange} maxLength={500} rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base resize-none" placeholder="Опишите ваш запрос или вопрос (до 500 символов)" />
                    <p className="text-xs text-gray-500 mt-1">{formData.comments.length}/500 символов</p>
                  </div>

                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="opacity-0 absolute -z-10 pointer-events-none" />

                  {errorMessage && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{errorMessage}</div>
                  )}
                  {formStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center"><i className="ri-check-line mr-1.5 text-lg"></i> Спасибо! Ваша заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.</div>
                  )}

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input type="checkbox" checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600 cursor-pointer flex-shrink-0" />
                    <span className="text-sm text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">Я согласен(на) на <a href="https://sivko.by/load/privacy-policy.pdf" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 underline">обработку персональных данных</a></span>
                  </label>

                  <button type="submit" disabled={formStatus === 'submitting' || !privacyAccepted} className="w-full bg-teal-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer">
                    {formStatus === 'submitting' ? 'Отправка...' : 'Отправить заявку'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-16">
            Часто задаваемые вопросы
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button onClick={() => toggleFaq(i)} className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer">
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{item.q}</h3>
                  <i className={`text-2xl text-teal-600 flex-shrink-0 ${openFaq === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                </button>
                <div className={`px-8 pb-6 ${openFaq === i ? '' : 'hidden'}`}>
                  <p className="text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}